import { useEffect, useRef, useState } from "react";
import { Compass, Route, PenTool, Workflow, ShieldCheck, LineChart, ArrowRight } from "lucide-react";

const ACCENT = "#BEF264";

const STEPS = [
  { Icon: Compass, label: "Understand the Business", text: "Identify the business goal, customer segments and main sales challenges." },
  { Icon: Route, label: "Map the Customer Journey", text: "Define what customers should see, what actions they should take and what happens after each action." },
  { Icon: PenTool, label: "Design and Build", text: "Create the website or landing page and configure the agreed customer-facing experience." },
  { Icon: Workflow, label: "Connect the Sales System", text: "Configure lead capture, CRM, pipeline stages and appropriate follow-up workflows." },
  { Icon: ShieldCheck, label: "Test and Launch", text: "Test the website, mobile journey, forms, integrations, communications and tracking." },
  { Icon: LineChart, label: "Measure and Improve", text: "Review enquiries, conversion performance and opportunities for improvement within the agreed support scope." },
];

export default function SalesJourney() {
  const [active, setActive] = useState(-1);
  const ref = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const el = ref.current;
    if (!el) return;
    let timer;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      if (reduced) { setActive(STEPS.length); return; }
      let i = 0;
      timer = setInterval(() => { i += 1; setActive(i); if (i >= STEPS.length) clearInterval(timer); }, 280);
    }, { threshold: 0.3 });
    io.observe(el);
    return () => { io.disconnect(); clearInterval(timer); };
  }, []);

  return (
    <section id="how-it-works" aria-labelledby="how-it-works-heading" data-testid="sales-journey-section" className="relative py-20 sm:py-28 bg-zinc-50 border-y border-zinc-200">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-600 font-semibold mb-6">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#0B1120" }} /> Website and CRM implementation
          </div>
          <h2 id="how-it-works-heading" className="font-display text-4xl sm:text-5xl lg:text-[56px] font-black tracking-[-0.03em] leading-[1.03] text-zinc-900">
            How Rekisa Builds Your Website and Sales System
          </h2>
        </div>

        <ol ref={ref} className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map(({ Icon, label, text }, i) => {
            const on = active > i;
            return (
              <li key={label} data-testid={`sales-step-${i + 1}`}
                className={`relative rounded-2xl border p-6 bg-white transition-[border-color,box-shadow] duration-500 ${on ? "border-zinc-900/20 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.35)]" : "border-zinc-200"}`}>
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center transition-colors duration-500"
                    style={on ? { background: "#0B1120", color: ACCENT } : { background: "#f4f4f5", color: "#71717a" }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs font-bold text-zinc-500">Step {i + 1}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-black text-zinc-900">{label}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-600">{text}</p>
              </li>
            );
          })}
        </ol>

        <div className="mt-10">
          <a href="#request-plan" data-testid="sales-journey-cta"
            className="inline-flex items-center gap-1.5 rounded-full min-h-12 px-7 py-3 text-base font-bold text-white bg-[#0B1120] hover:brightness-125 transition-[filter]">
            Request My Sales System Plan <ArrowRight className="h-5 w-5" style={{ color: ACCENT }} />
          </a>
        </div>
      </div>
    </section>
  );
}
